import React from 'react';
import Layout from '../components/Layout';
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { 
  ArrowRight, 
  GraduationCap, 
  Briefcase, 
  Globe, 
  MapPin,
  Quote,
  ExternalLink
} from 'lucide-react';

import thomasPortrait from "../assets/thomas_portrait.jpg";
import thomasFushimi from "../assets/thomas_fushimi.jpg";
import thomasExhibition from "../assets/thomas_exhibition.jpg";
import gregoireJapan from "../assets/gregoire_japan.jpg";
import diegoTokyo from "../assets/diego_tokyo.png";
import yoannJapan from "../assets/yoann_japan.jpg";
import mirabelleJapan from "../assets/mirabelle_japan.jpg";

const stories = [
  {
    name: "Thomas",
    role: "Stagiaire en Ingénierie Logicielle",
    school: "École d'ingénieurs, Lyon",
    company: "Sound & Vision Technologies (Care Cube)",
    location: "Osaka, Japon",
    duration: "6 mois",
    img: thomasPortrait,
    imgAlt: "Thomas, stagiaire ingénieur de Lyon",
    quote: "Ce stage m'a appris l'importance du travail d'équipe et comment résoudre les problèmes de manière collaborative. Je suis vraiment content d'avoir choisi de venir au Japon.",
    highlights: [
      "Construction d'une application iOS de zéro pour le produit principal de l'entreprise",
      "Participation active à des expositions technologiques, présentant le travail à des professionnels",
      "Exploration de la culture japonaise à travers des visites à Kyoto, Fushimi Inari et au-delà"
    ],
    takeaway: "Vivre et travailler à l'étranger m'a aidé à développer de meilleures compétences d'autogestion. M'adapter à un nouvel environnement m'a donné une plus grande confiance pour relever de nouveaux défis.",
    link: "https://medium.com/@CareCube_/crossing-borders-to-japan-thomass-journey-as-an-engineering-intern-d4e31a2848f3",
    featured: true,
    galleryImages: [
      { src: thomasExhibition, alt: "Thomas lors d'une exposition tech" },
      { src: thomasFushimi, alt: "Fushimi Inari Taisha, Kyoto" }
    ]
  },
  {
    name: "Grégoire",
    role: "Étudiant en Échange Académique",
    school: "ICAM Strasbourg-Europe",
    company: "Shibaura Institute of Technology",
    location: "Tokyo, Japon",
    duration: "1 semestre",
    img: gregoireJapan,
    imgAlt: "Grégoire lors de son échange au Japon",
    quote: "J'ai choisi le Japon car ses universités offrent des opportunités pour explorer la science de pointe, la robotique et l'IA. Je voulais aussi sortir de ma zone de confort et m'immerger dans une culture totalement différente.",
    highlights: [
      "Études au Shibaura Institute of Technology, l'une des principales universités technologiques du Japon",
      "Exploration de la robotique, de l'intelligence artificielle et de sujets d'ingénierie avancée",
      "Participation à la vie étudiante : sports, activités culturelles et découverte de Tokyo"
    ],
    takeaway: "Mon conseil aux étudiants intéressés par le Japon : n'attendez pas, foncez ! Apprenez le japonais pour rendre l'expérience encore plus riche et mémorable.",
    link: "https://www.icam.fr/temoignage/gregoire-temoignage-dun-echange-academique-au-japon/"
  },
  {
    name: "Diego",
    role: "Stagiaire Analyste de Données",
    school: "UCLy / ESTRI Lyon",
    company: "MSC Japan (Mediterranean Shipping Company)",
    location: "Tokyo, Japon",
    duration: "4 à 6 mois",
    img: diegoTokyo,
    imgAlt: "Diego pendant son stage à Tokyo",
    quote: "J'ai pu mettre en pratique les cours de langues et d'interculturalité dans un contexte concret. J'en ressors plus autonome, plus adaptable, et avec une ouverture culturelle décuplée.",
    highlights: [
      "Analyste de données chez MSC Japan, collecte et reporting de données pour les responsables régionaux",
      "Environnement de travail entièrement international et anglophone",
      "Combinaison d'immersion professionnelle et de découverte culturelle approfondie du Japon"
    ],
    takeaway: "Professionnellement, c'est une ligne forte sur mon CV. Personnellement, j'en ressors plus autonome, plus adaptable, et avec une perspective culturelle bien plus large.",
    link: "https://www.ucly.fr/lience-lea-le-stage-de-diego-a-tokyo/"
  },
  {
    name: "Yoann",
    role: "Stagiaire Systèmes Embarqués & Réalisateur",
    school: "CESI Toulouse",
    company: "Startup IoT franco-japonaise",
    location: "Shibuya, Tokyo, Japon",
    duration: "3 mois",
    img: yoannJapan,
    imgAlt: "Yoann pendant son stage au Japon",
    quote: "Voyager seul à l'autre bout du monde est déstabilisant, mais ces expériences sont celles qui permettent de vraiment grandir et de se découvrir.",
    highlights: [
      "Développement d'un capteur de qualité de l'eau connecté dans une startup IoT à Shibuya",
      "Création de 'Yūgen', un documentaire long-métrage sur le Japon, projeté en première dans le plus grand cinéma de Toulouse",
      "Constitution d'une équipe d'environ 20 personnes, développant ses compétences en leadership et gestion de projet"
    ],
    takeaway: "Gérer une équipe, piloter un projet long et complexe, apprendre à déléguer — ce sont des compétences essentielles quelle que soit la profession. Cette expérience complète parfaitement ma formation d'ingénieur.",
    link: "https://www.cesi.fr/en/testimonials/yugen-international-internship-japan/"
  },
  {
    name: "Mirabelle",
    role: "Stagiaire Ingénieure Chimiste",
    school: "CPE Lyon",
    company: "Air Liquide Laboratories",
    location: "Tsukuba, Japon",
    duration: "Année de césure (12 mois)",
    img: mirabelleJapan,
    imgAlt: "Une pagode au Japon, photographiée lors de l'année de césure de Mirabelle",
    quote: "Cette expérience à l'étranger est le meilleur choix que je pouvais faire ! Partir loin permet de prendre un certain recul sur sa vie, sur ce qu'on croyait universel.",
    highlights: [
      "Travail sur les dépôts de films chimiques (ALD/CVD) pour la fabrication de semi-conducteurs chez Air Liquide",
      "Collaboration dans une équipe véritablement multiculturelle : collègues français, espagnols, mexicains, coréens et japonais",
      "Cours de japonais offerts par l'entreprise, facilitant l'intégration au quotidien"
    ],
    takeaway: "J'ai énormément appris — non seulement sur mon travail, mais aussi sur des sujets auxquels je ne me serais pas intéressée de moi-même. Les conversations avec mes collègues m'ont appris sur leurs cultures et leur façon de voir la vie.",
    link: "https://www.cpe.fr/article/aa-international-partir-cgp-eti-stages-et-annee-de-cesure-temoignages-annees-de-cesure-mirabelle-denavit-japon/"
  }
];

const FeaturedStory = ({ story }) => (
  <div data-testid="featured-story" className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden mb-16">
    <div className="grid grid-cols-1 lg:grid-cols-5">
      <div className="lg:col-span-2 relative h-72 lg:h-auto min-h-[320px]">
        <img src={story.img} alt={story.imgAlt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent lg:bg-gradient-to-r"></div>
        <div className="absolute bottom-6 left-6">
          <Badge className="bg-red-600 text-white border-none mb-2">En vedette</Badge>
          <p className="text-white font-bold text-2xl">{story.name}</p>
          <p className="text-white/80 text-sm">{story.role}</p>
        </div>
      </div>
      <div className="lg:col-span-3 p-8 lg:p-10">
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <GraduationCap className="h-4 w-4" /> {story.school}
        </div>
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <Briefcase className="h-4 w-4" /> {story.company}
        </div>
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <MapPin className="h-4 w-4" /> {story.location} &middot; {story.duration}
        </div>

        <blockquote className="mt-6 text-lg text-slate-700 italic leading-relaxed border-l-4 border-red-500 pl-4 mb-6">
          "{story.quote}"
        </blockquote>

        <h4 className="font-bold text-slate-900 mb-3">Points forts</h4>
        <ul className="space-y-2 mb-6">
          {story.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <ArrowRight className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              {h}
            </li>
          ))}
        </ul>

        {story.galleryImages && (
          <div className="flex gap-3 mb-6">
            {story.galleryImages.map((img, i) => (
              <div key={i} className="w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        <a
          href={story.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="featured-story-link"
          className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors group"
        >
          Lire l'article complet sur Medium
          <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </div>
  </div>
);

const StoryCard = ({ story }) => (
  <Card data-testid={`story-card-${story.name.toLowerCase()}`} className="overflow-hidden border-slate-100 hover:shadow-xl hover:border-red-200 transition-all duration-300 flex flex-col">
    <div className="relative h-52 overflow-hidden">
      <img src={story.img} alt={story.imgAlt} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
      <div className="absolute bottom-3 left-4">
        <p className="text-white font-bold text-xl">{story.name}</p>
        <p className="text-white/80 text-xs">{story.school}</p>
      </div>
    </div>
    <CardContent className="p-6 flex flex-col flex-1">
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Briefcase className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span className="font-medium">{story.role}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          {story.company}, {story.location}
        </div>
        <p className="text-xs text-slate-400">{story.duration}</p>
      </div>

      <blockquote className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-red-300 pl-3 mb-4 flex-1">
        "{story.quote}"
      </blockquote>

      <h4 className="font-semibold text-slate-900 text-sm mb-2">Points forts</h4>
      <ul className="space-y-1.5 mb-5">
        {story.highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
            <ArrowRight className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
            {h}
          </li>
        ))}
      </ul>

      <p className="text-xs text-slate-500 italic mb-4 bg-slate-50 rounded-lg p-3">
        <Quote className="h-3 w-3 text-red-400 inline mr-1" />
        {story.takeaway}
      </p>

      <a
        href={story.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-red-600 text-sm font-semibold hover:text-red-700 transition-colors group mt-auto"
      >
        Lire l'article complet
        <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </a>
    </CardContent>
  </Card>
);

const TestimonialsFR = () => {
  const featured = stories.find(s => s.featured);
  const others = stories.filter(s => !s.featured);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-20 pb-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">Témoignages</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Des Histoires Vraies de Vrais Stagiaires</h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Des étudiants français partagent leurs expériences de travail et de vie au Japon — des laboratoires de Tsukuba aux startups IoT de Shibuya.
          </p>
        </div>
      </section>

      {/* Featured Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <FeaturedStory story={featured} />

          <h2 className="text-2xl font-bold text-slate-900 mb-8">Plus d'Expériences</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {others.map((story, i) => (
              <StoryCard key={i} story={story} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-2xl">
          <h2 className="text-3xl font-bold mb-4">Votre Histoire Pourrait Être la Prochaine</h2>
          <p className="text-slate-300 mb-8">
            Que vous soyez une entreprise à la recherche de talents internationaux exceptionnels ou un étudiant rêvant du Japon — Internjin le rend possible.
          </p>
          <a 
            href="mailto:contact@internjin.com"
            data-testid="testimonials-cta"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-full transition-colors"
          >
            Contactez-nous <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default TestimonialsFR;
